<template>
	<div
		ref="filtersList"
		class="filters-list"
	>
		<Modal
			:title="title"
			:type="modalType"
			:blocking="true"
			:open="isOpen"
			:footer-classes="{vertical: isVertical}"
			@close="onClose"
			@cancel="onCancel"
		>
			<template #body>
				<div
					v-if="isMobileWebApp"
					class="filters-list--row"
					:class="{ active: hasFilters }"
					@click="resetFilters"
				>
					{{ resetFiltersLabel }}
				</div>
				<div
					v-if="hasKeyword"
					class="search-keyword"
				>
					{{ labels.activeSearch }}: <strong>&quot;{{ searchKeyword }}&quot;</strong>
				</div>
				<FiltersListSection
					:clip-filters="tagFilters"
					:title="transChoiceUcFirst('commons.tag', 2)"
					:value="activeTagFilters"
					:hide-disabled="true"
					:initially-open="true"
					@input="updateActiveTagFilters"
				/>

				<FiltersListSection
					:clip-filters="ratingFilters"
					:title="trans('commons.rating')"
					:value="activeRatingFilters"
					:hide-disabled="false"
					:initially-open="true"
					@input="updateActiveRatingFilters"
				/>

				<AthleteFiltersSection
					v-if="isPlayerCompetition"
					:clip-filters="allPlayerFilters"
					:value="allActivePlayerFilters"
					:title="athleteLabel"
					@input="updateActivePlayerFilters"
				/>

				<template v-if="!isPlayerCompetition">
					<TeamFiltersSection
						v-for="(team, idx) in eventTeams"
						:key="`team_filter_${idx}`"
						:team="team"
						:clip-filters="teamAndPlayerFilters(team)"
						:title="team.realName"
						:value="teamAndPlayerActiveFilters(team)"
						@input="updateActiveTeamAndPlayerFilters"
					/>
				</template>
			</template>
			<template
				v-if="isMobileWebApp"
				#footer-buttons
			>
				<button
					class="button button--responsive button--primary modal-close"
					@click="applyFilters"
				>
					{{ labels.applyFilters }}
				</button>
			</template>
			<template
				v-else-if="isVertical"
				#footer-buttons
			>
				<button
					class="button button--responsive button--primary modal-close"
					@click="applyFilters"
				>
					{{ labels.applyFilters }}
				</button>
				<button
					class="button button--responsive button--secondary"
					:disabled="!hasFilters"
					@click="resetFilters"
				>
					{{ resetFiltersLabel }}
				</button>
			</template>
			<template
				v-else
				#footer-buttons
			>
				<button
					class="button button--responsive button--secondary"
					:disabled="!hasFilters"
					@click="resetFilters"
				>
					{{ resetFiltersLabel }}
				</button>
				<button
					class="button button--responsive button--primary modal-close"
					@click="applyFilters"
				>
					{{ labels.applyFilters }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<script>
import Modal from '../modal/Modal';
import FiltersListSection from './FiltersListSection';
import { FILTER_GROUP_TAG, FILTER_GROUP_RATING } from '../../store/modules/clip-filters';
import { mapGetters } from 'vuex';
import TeamFiltersSection from './TeamFiltersSection';
import AthleteFiltersSection from './AthleteFiltersSection';

export default {
	name:       'FiltersList',
	components: { AthleteFiltersSection, TeamFiltersSection, FiltersListSection, Modal },
	props:      {
		title: {
			type:    String,
			default: '',
		},
		isOpen: {
			type: Boolean,
		},
		moduleName: {
			type:    String,
			default: 'clipFilters',
		},
		searchKeyword: {
			type: String,
		},
	},
	data() {
		return {
			initialFilters: [],
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:           'app/isMobileWebApp',
			isViewModeOrFullPageMode: 'app/isViewModeOrFullPageMode',
			isDesktopWebApp:          'app/isDesktopWebApp',
			event:                    'event/event',
			isPlayerCompetition:      'event/isPlayerCompetition',
			isMobilePortrait:         'device/isMobilePortrait',
		}),
		allFilters() {
			return this.$store.getters[`${this.moduleName}/groupedFilters`];
		},
		filteredClips() {
			return this.$store.getters[`${this.moduleName}/filteredClips`];
		},
		activeFilters() {
			return this.$store.getters[`${this.moduleName}/activeFilters`];
		},
		activeTagFilters() {
			return this.$store.getters[`${this.moduleName}/activeTagFilters`];
		},
		activeRatingFilters() {
			return this.$store.getters[`${this.moduleName}/activeRatingFilters`];
		},
		allActiveTeamFilters() {
			return this.$store.getters[`${this.moduleName}/allActiveTeamFilters`];
		},
		allPlayerFilters() {
			return this.$store.getters[`${this.moduleName}/allPlayerFilters`];
		},
		allActivePlayerFilters() {
			return this.$store.getters[`${this.moduleName}/allActivePlayerFilters`];
		},
		labels() {
			return {
				applyFilters: this.hasFilters
					? `${this.filteredClips.length} ${this.transChoiceUcFirst('commons.clips_selected', this.filteredClips.length)}`
					: `${this.transUcfirst('commons.clips_show_all')}`,
				activeSearch: this.transUcfirst('commons.active_search_term'),
			};
		},
		tagFilters() {
			return this.allFilters[FILTER_GROUP_TAG];
		},
		ratingFilters() {
			return this.allFilters[FILTER_GROUP_RATING];
		},
		hasFilters() {
			return this.activeFilters.length > 0;
		},
		athleteLabel() {
			return this.transUcfirst('commons.athlete_select');
		},
		resetFiltersLabel() {
			return this.transUcfirst('commons.filters_reset');
		},
		isVertical() {
			return this.isViewModeOrFullPageMode && this.isDesktopWebApp;
		},
		eventTeams() {
			return this.event.teams;
		},
		hasKeyword() {
			return this.searchKeyword?.length > 2;
		},
		modalType() {
			return this.isMobilePortrait ? 'fullscreen' : 'modal-md';
		},
	},
	mounted() {
		this.$store.dispatch(`${this.moduleName}/init`);
		this.initialFilters = [...this.activeFilters];
	},
	beforeDestroy() {
		this.$refs.filtersList.remove();
	},
	methods: {
		teamAndPlayerFilters(team) {
			return this.$store.getters[`${this.moduleName}/teamAndPlayerFilters`](team);
		},
		teamAndPlayerActiveFilters(team) {
			return this.$store.getters[`${this.moduleName}/activeTeamAndPlayerFilters`](team);
		},
		updateActiveFilters(filters) {
			this.$store.dispatch(`${this.moduleName}/updateActiveFilters`, filters);
		},
		updateActiveTagFilters(filters) {
			this.updateActiveFilters([
				...filters,
				...this.allActiveTeamFilters,
				...this.allActivePlayerFilters,
				...this.activeRatingFilters,
			]);
		},
		updateActiveRatingFilters(filters) {
			this.updateActiveFilters([
				...filters,
				...this.activeTagFilters,
				...this.allActiveTeamFilters,
				...this.allActivePlayerFilters,
			]);
		},
		updateActiveTeamAndPlayerFilters(team, filters) {
			this.$store.dispatch(`${this.moduleName}/updateActiveTeamAndPlayerFilters`, { team, filters });
		},
		updateActivePlayerFilters(filters) {
			return this.updateActiveFilters([
				...this.activeTagFilters,
				...this.activeRatingFilters,
				...filters,
			]);
		},
		async onClose() {
			this.$emit('close');
		},
		async onCancel() {
			await this.revertChanges();
			this.$emit('close');
		},
		applyFilters() {
			this.$emit('filtered-items-updated', this.filteredClips);
			this.$emit('filters-updated', this.activeFilters);
			this.onClose();
		},
		resetFilters() {
			this.updateActiveFilters([]);
		},
		revertChanges() {
			this.$store.dispatch(`${this.moduleName}/updateActiveFilters`, this.initialFilters);
		},
		syncModalState() {
			if (this.isOpen) {
				window.eventBus.$emit('modal:filter-list');
			} else {
				window.eventBus.$emit('modalClose:filter-list');
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts.scss';

.filters-list::v-deep {
	.modal--content {
		display: flex;
		flex-direction: column;
	}

	.modal--body {
		flex-grow: 1;
		overflow-y: auto;
	}

	.is-desktop & {
		.modal--footer-buttons button {
			flex: 0 1 auto;
		}

		.modal--footer {
			border-top: 1px solid var(--color-grey-800);
		}
	}
}

.filters-list--row {
	margin-bottom: 24px;
	color: var(--color-grey-300);

	&.active {
		color: var(--color-hype-yellow);
		cursor: pointer;
	}
}

.search-keyword {
	@include text-md-2;
	margin-bottom: 24px;
}
</style>
